// Utility functions for secure storage of sensitive data

const TENANT_ID_KEY = 'tenant_id';

/**
 * Save tenant ID securely in sessionStorage
 * Using sessionStorage instead of localStorage for better security
 * Data will be cleared when the browser tab is closed
 */
export const saveTenantId = (tenantId: string): void => {
  try {
    sessionStorage.setItem(TENANT_ID_KEY, tenantId);
  } catch (error) {
    console.error('Failed to save tenant ID:', error);
    throw new Error('Failed to save tenant ID');
  }
};

/**
 * Retrieve tenant ID from sessionStorage
 */
export const getTenantId = (): string | null => {
  try {
    return sessionStorage.getItem(TENANT_ID_KEY);
  } catch (error) {
    console.error('Failed to retrieve tenant ID:', error);
    return null;
  }
};

/**
 * Remove tenant ID from sessionStorage
 */
export const removeTenantId = (): void => {
  try {
    sessionStorage.removeItem(TENANT_ID_KEY);
  } catch (error) {
    console.error('Failed to remove tenant ID:', error);
  }
};

/**
 * Check if tenant ID exists in storage
 */
export const hasTenantId = (): boolean => {
  return getTenantId() !== null;
};

const API_KEY_KEY = 'api_key';

/**
 * Save API key securely in sessionStorage
 */
export const saveApiKey = (apiKey: string): void => {
  try {
    sessionStorage.setItem(API_KEY_KEY, apiKey);
  } catch (error) {
    console.error('Failed to save API key:', error);
  }
};

/**
 * Retrieve API key from sessionStorage
 */
export const getApiKey = (): string | null => {
  try {
    return sessionStorage.getItem(API_KEY_KEY);
  } catch (error) {
    console.error('Failed to retrieve API key:', error);
    return null;
  }
};

/**
 * Remove API key from sessionStorage
 */
export const removeApiKey = (): void => {
  try {
    sessionStorage.removeItem(API_KEY_KEY);
  } catch (error) {
    console.error('Failed to remove API key:', error);
  }
};

const CONNECTION_ID_KEY = 'connection_id';

/**
 * Save connection ID securely in sessionStorage
 */
export const saveConnectionId = (connectionId: string): void => {
  try {
    sessionStorage.setItem(CONNECTION_ID_KEY, connectionId);
  } catch (error) {
    console.error('Failed to save connection ID:', error);
    throw new Error('Failed to save connection ID');
  }
};

/**
 * Retrieve connection ID from sessionStorage
 */
export const getConnectionId = (): string | null => {
  try {
    return sessionStorage.getItem(CONNECTION_ID_KEY);
  } catch (error) {
    console.error('Failed to retrieve connection ID:', error);
    return null;
  }
};

/**
 * Check if connection ID exists in storage
 */
export const hasConnectionId = (): boolean => {
  return getConnectionId() !== null;
};

/**
 * Remove connection ID from sessionStorage
 */
export const removeConnectionId = (): void => {
  try {
    sessionStorage.removeItem(CONNECTION_ID_KEY);
  } catch (error) {
    console.error('Failed to remove connection ID:', error);
  }
};

const ACTIVE_COLLECTION_KEY_PREFIX = 'active_collection_id';

const getCollectionStorageKey = (connectionId?: string | null): string => {
  return connectionId ? `${ACTIVE_COLLECTION_KEY_PREFIX}_${connectionId}` : ACTIVE_COLLECTION_KEY_PREFIX;
};

export const saveActiveCollectionId = (collectionId: string, connectionId?: string | null): void => {
  try {
    sessionStorage.setItem(getCollectionStorageKey(connectionId), collectionId);
  } catch (error) {
    console.error('Failed to save active collection ID:', error);
  }
};

export const getActiveCollectionId = (connectionId?: string | null): string | null => {
  try {
    return sessionStorage.getItem(getCollectionStorageKey(connectionId));
  } catch (error) {
    console.error('Failed to retrieve active collection ID:', error);
    return null;
  }
};

export const removeActiveCollectionId = (connectionId?: string | null): void => {
  try {
    sessionStorage.removeItem(getCollectionStorageKey(connectionId));
  } catch (error) {
    console.error('Failed to remove active collection ID:', error);
  }
};

const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_DATA_KEY = 'user_data';

/**
 * Save access token securely in sessionStorage
 */
export const saveAccessToken = (token: string): void => {
  try {
    sessionStorage.setItem(ACCESS_TOKEN_KEY, token);
  } catch (error) {
    console.error('Failed to save access token:', error);
    throw new Error('Failed to save access token');
  }
};

/**
 * Retrieve access token from sessionStorage
 */
export const getAccessToken = (): string | null => {
  try {
    return sessionStorage.getItem(ACCESS_TOKEN_KEY);
  } catch (error) {
    console.error('Failed to retrieve access token:', error);
    return null;
  }
};

/**
 * Remove access token from sessionStorage
 */
export const removeAccessToken = (): void => {
  try {
    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  } catch (error) {
    console.error('Failed to remove access token:', error);
  }
};

/**
 * Save refresh token securely in sessionStorage
 */
export const saveRefreshToken = (token: string): void => {
  try {
    sessionStorage.setItem(REFRESH_TOKEN_KEY, token);
  } catch (error) {
    console.error('Failed to save refresh token:', error);
    throw new Error('Failed to save refresh token');
  }
};

/**
 * Retrieve refresh token from sessionStorage
 */
export const getRefreshToken = (): string | null => {
  try {
    return sessionStorage.getItem(REFRESH_TOKEN_KEY);
  } catch (error) {
    console.error('Failed to retrieve refresh token:', error);
    return null;
  }
};

/**
 * Remove refresh token from sessionStorage
 */
export const removeRefreshToken = (): void => {
  try {
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch (error) {
    console.error('Failed to remove refresh token:', error);
  }
};

/**
 * Save user data securely in sessionStorage
 */
export const saveUserData = (userData: any): void => {
  try {
    sessionStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
  } catch (error) {
    console.error('Failed to save user data:', error);
    throw new Error('Failed to save user data');
  }
};

/**
 * Retrieve user data from sessionStorage
 */
export const getUserData = (): any | null => {
  try {
    const data = sessionStorage.getItem(USER_DATA_KEY);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error('Failed to retrieve user data:', error);
    return null;
  }
};

/**
 * Remove user data from sessionStorage
 */
export const removeUserData = (): void => {
  try {
    sessionStorage.removeItem(USER_DATA_KEY);
  } catch (error) {
    console.error('Failed to remove user data:', error);
  }
};

/**
 * Get user ID from stored user data or decode from JWT token
 */
export const getUserId = (): string | null => {
  try {
    // First try to get from user data
    const userData = getUserData();
    if (userData && userData.user_id !== undefined) {
      return String(userData.user_id);
    }

    // Fallback: decode from JWT token
    const token = getAccessToken();
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.user_id !== undefined) {
          return String(payload.user_id);
        }
      } catch (e) {
        console.error('Failed to decode JWT token:', e);
      }
    }

    return null;
  } catch (error) {
    console.error('Failed to get user ID:', error);
    return null;
  }
};

/**
 * Clear all stored data (logout)
 * Removes everything from both sessionStorage and localStorage
 */
export const clearAllStorage = (): void => {
  try {
    // Clear all sessionStorage
    sessionStorage.clear();
    
    // Clear all localStorage
    localStorage.clear();
    
    console.log('All storage cleared successfully');
  } catch (error) {
    console.error('Failed to clear storage:', error);
  }
};
